import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Location } from '@/types/models';
import { toast } from 'sonner';
import { Pencil, Plus, Trash2 } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import * as locationService from '@/services/locationService';
import { LocationFormValues } from '@/types/locationTypes';
import { useQueryClient } from '@tanstack/react-query';

interface LocationsManagerProps {
  locations: Location[];
  setLocations: React.Dispatch<React.SetStateAction<Location[]>>;
  organizationId: string;
}

const LocationsManager: React.FC<LocationsManagerProps> = ({ locations, setLocations, organizationId }) => {
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [currentLocation, setCurrentLocation] = useState<Location | null>(null);
  const [newLocationName, setNewLocationName] = useState('');
  const [newLocationAddress, setNewLocationAddress] = useState('');
  const [newLocationType, setNewLocationType] = useState<string>('business');
  const [newLocationNotes, setNewLocationNotes] = useState('');
  const [newLocationCity, setNewLocationCity] = useState('');
  const [newLocationState, setNewLocationState] = useState('');
  const [newLocationZip, setNewLocationZip] = useState('');
  const queryClient = useQueryClient();

  // Map UI type values to database type values
  const mapTypeValue = (uiType: string): 'business' | 'external' | 'virtual' => {
    switch(uiType) {
      case 'business': return 'business';
      case 'external': return 'external';
      case 'virtual': return 'virtual';
      default: return 'business';
    }
  };

  const handleCreateLocation = async (locationData: LocationFormValues) => {
    try {
      const newLocation: Omit<Location, 'id' | 'created_at'> = {
        organization_id: organizationId,
        name: locationData.name,
        address: locationData.address,
        city: locationData.city,
        state: locationData.state,
        zip: locationData.zip,
        type: mapTypeValue(locationData.type || 'business'),
        notes: locationData.notes
      };
      
      const location = await locationService.createLocation(newLocation);
      
      if (location) {
        setLocations([...locations, location]);
        toast.success('Location created successfully');
        setIsAddDialogOpen(false);
        queryClient.invalidateQueries({ queryKey: ['locations'] });
      } else {
        toast.error('Failed to create location');
      }
    } catch (error) {
      console.error('Error creating location:', error);
      toast.error('Failed to create location');
    }
  };

  const handleEditLocation = async () => {
    if (!currentLocation || !newLocationName || !newLocationAddress) {
      toast.error('Name and address are required');
      return;
    }

    try {
      const updatedLocation = await locationService.updateLocation(
        currentLocation.id,
        {
          name: newLocationName,
          address: newLocationAddress,
          city: newLocationCity,
          state: newLocationState,
          zip: newLocationZip,
          type: mapTypeValue(newLocationType),
          notes: newLocationNotes
        }
      );

      if (updatedLocation) {
        setLocations(locations.map(loc => 
          loc.id === currentLocation.id ? updatedLocation : loc
        ));
        resetForm();
        setIsEditDialogOpen(false);
        toast.success('Location updated successfully');
        queryClient.invalidateQueries({ queryKey: ['locations'] });
      } else {
        throw new Error('Failed to update location');
      }
    } catch (error) {
      console.error('Error updating location:', error);
      toast.error('Failed to update location');
    }
  };

  const handleDeleteLocation = async () => {
    if (!currentLocation) return;

    try {
      const success = await locationService.deleteLocation(currentLocation.id);
      
      if (success) {
        setLocations(locations.filter(loc => loc.id !== currentLocation.id));
        setIsDeleteDialogOpen(false);
        toast.success('Location deleted successfully');
        queryClient.invalidateQueries({ queryKey: ['locations'] });
      } else {
        throw new Error('Failed to delete location');
      }
    } catch (error) {
      console.error('Error deleting location:', error);
      toast.error('Failed to delete location');
    }
  };

  const openEditDialog = (location: Location) => {
    setCurrentLocation(location);
    setNewLocationName(location.name);
    setNewLocationAddress(location.address);
    setNewLocationType(location.type || 'business');
    setNewLocationNotes(location.notes || '');
    setIsEditDialogOpen(true);
  };

  const openDeleteDialog = (location: Location) => {
    setCurrentLocation(location);
    setIsDeleteDialogOpen(true);
  };

  const resetForm = () => {
    setNewLocationName('');
    setNewLocationAddress('');
    setNewLocationType('business');
    setNewLocationNotes('');
    setCurrentLocation(null);
  };

  const handleAddDialogSubmit = () => {
    const locationData: LocationFormValues = {
      name: newLocationName,
      address: newLocationAddress,
      city: newLocationCity,
      state: newLocationState,
      zip: newLocationZip,
      type: mapTypeValue(newLocationType),
      notes: newLocationNotes
    };
    
    handleCreateLocation(locationData);
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-xl font-semibold">Locations</h2>
        <Button onClick={() => setIsAddDialogOpen(true)}>
          <Plus className="h-4 w-4 mr-2" />
          Add Location
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {locations.map((location) => (
          <Card key={location.id} className="overflow-hidden">
            <CardHeader className="bg-muted/10 border-b border-border/20">
              <CardTitle className="text-lg">{location.name}</CardTitle>
            </CardHeader>
            <CardContent className="pt-4">
              <div className="space-y-2">
                <p className="text-sm"><span className="font-medium">Address:</span> {location.address}</p>
                {location.type && (
                  <p className="text-sm"><span className="font-medium">Type:</span> {location.type}</p>
                )}
                {location.notes && (
                  <p className="text-sm text-muted-foreground">
                    <span className="font-medium">Notes:</span> {location.notes}
                  </p>
                )}
              </div>
            </CardContent>
            <CardFooter className="flex justify-end space-x-2">
              <Button variant="ghost" size="sm" onClick={() => openEditDialog(location)}>
                <Pencil className="h-4 w-4" />
              </Button>
              <Button variant="destructive" size="icon" onClick={() => openDeleteDialog(location)}>
                <Trash2 className="h-4 w-4" />
              </Button>
            </CardFooter>
          </Card>
        ))}
      </div>

      {/* Add Location Dialog */}
      <Dialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Location</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="name">Location Name</Label>
              <Input
                id="name"
                value={newLocationName}
                onChange={(e) => setNewLocationName(e.target.value)}
                placeholder="Business Location One"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="address">Address</Label>
              <Input
                id="address"
                value={newLocationAddress}
                onChange={(e) => setNewLocationAddress(e.target.value)}
                placeholder="123 Main St., Suite 101"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="city">City</Label>
                <Input
                  id="city"
                  value={newLocationCity}
                  onChange={(e) => setNewLocationCity(e.target.value)}
                  placeholder="City"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="state">State</Label>
                <Input
                  id="state"
                  value={newLocationState}
                  onChange={(e) => setNewLocationState(e.target.value)}
                  placeholder="State"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="zip">ZIP Code</Label>
              <Input
                id="zip"
                value={newLocationZip}
                onChange={(e) => setNewLocationZip(e.target.value)}
                placeholder="ZIP"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="type">Type</Label>
              <Select
                value={newLocationType}
                onValueChange={setNewLocationType}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="business">Business</SelectItem>
                  <SelectItem value="external">External Venue</SelectItem>
                  <SelectItem value="virtual">Virtual/Online</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="notes">Notes (Optional)</Label>
              <Textarea
                id="notes"
                value={newLocationNotes}
                onChange={(e) => setNewLocationNotes(e.target.value)}
                placeholder="Any additional details about this location..."
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => {
              resetForm();
              setIsAddDialogOpen(false);
            }}>
              Cancel
            </Button>
            <Button onClick={handleAddDialogSubmit}>Add Location</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Location Dialog */}
      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Location</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="edit-name">Location Name</Label>
              <Input
                id="edit-name"
                value={newLocationName}
                onChange={(e) => setNewLocationName(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-address">Address</Label>
              <Input
                id="edit-address"
                value={newLocationAddress}
                onChange={(e) => setNewLocationAddress(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-type">Type</Label>
              <Select
                value={newLocationType}
                onValueChange={setNewLocationType}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="business">Business</SelectItem>
                  <SelectItem value="external">External Venue</SelectItem>
                  <SelectItem value="virtual">Virtual/Online</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-notes">Notes (Optional)</Label>
              <Textarea
                id="edit-notes"
                value={newLocationNotes}
                onChange={(e) => setNewLocationNotes(e.target.value)}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => {
              resetForm();
              setIsEditDialogOpen(false);
            }}>
              Cancel
            </Button>
            <Button onClick={handleEditLocation}>Save Changes</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Location</DialogTitle>
          </DialogHeader>
          <p>
            Are you sure you want to delete {currentLocation?.name}? This action cannot be undone.
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsDeleteDialogOpen(false)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleDeleteLocation}>
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default LocationsManager;
